import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FaBuilding,
  FaFileAlt,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaPlusCircle,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function DepartmentForm({
  isOpen,
  onClose,
  department,
  departmentToEdit,
  onSuccess,
}) {
  const currentDepartment = department || departmentToEdit;
  const isEdit = !!currentDepartment;
  const { token } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      isActive: true,
    },
  });

  const isActiveValue = watch("isActive");

  useEffect(() => {
    if (currentDepartment) {
      reset({
        name: currentDepartment.name || currentDepartment.Name || "",
        description: currentDepartment.description || currentDepartment.Description || "",
        isActive: currentDepartment.isActive !== false,
      });
    } else {
      reset({
        name: "",
        description: "",
        isActive: true,
      });
    }
  }, [currentDepartment, reset, isOpen]);

  if (!isOpen) return null;

  const onSubmit = async (data) => {
    try {
      const url = isEdit
        ? `${server}/departments/updateById?departmentId=${currentDepartment.id || currentDepartment.Id}`
        : `${server}/departments/new`;

      const method = isEdit ? "put" : "post";

      const payload = {
        name: data.name,
        description: data.description,
        isActive: Boolean(data.isActive),
      };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(
        isEdit ? "Department updated successfully!" : "New Department created!",
      );
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error saving department record.",
      );
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        
        {/* Royal Blue Header Bar */}
        <div className="bg-[#1d5bd8] text-white p-5 sm:p-6 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-xs flex items-center justify-center text-white font-black shadow-xs border border-white/20">
              {isEdit ? <FaEdit size={18} /> : <FaPlusCircle size={18} />}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black tracking-tight text-white leading-tight">
                {isEdit ? "Edit Department" : "Add New Department"}
              </h2>
              <p className="text-xs text-blue-100 font-medium mt-0.5">
                {isEdit ? "Update department profile and operational status" : "Create academic faculty or staff division"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-2xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition cursor-pointer"
            aria-label="Close modal"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Form Fields */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          {/* Department Name */}
          <div className="space-y-1.5">
            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
              Department Name <span className="text-rose-500">*</span>
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                <FaBuilding size={14} />
              </div>
              <input
                {...register("name", { required: "Department name is required" })}
                className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border ${
                  errors.name
                    ? "border-rose-500 bg-rose-50/50"
                    : "border-gray-200 dark:border-slate-700 focus:border-blue-600 dark:focus:border-blue-500"
                } rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all`}
                placeholder="e.g. Science & Technology / Administration"
              />
              {errors.name && (
                <div className="absolute right-3 top-3 text-rose-500">
                  <FaExclamationCircle size={14} />
                </div>
              )}
            </div>
            {errors.name && (
              <p className="text-xs text-rose-500 font-bold ml-1 animate-pulse">
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
              Description <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <div className="relative group">
              <div className="absolute top-3 left-3.5 pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                <FaFileAlt size={13} />
              </div>
              <textarea
                {...register("description")}
                rows="3"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 outline-none text-xs font-semibold text-gray-900 dark:text-white transition-all resize-none"
                placeholder="Enter a brief summary of activities and staff responsibilities..."
              />
            </div>
          </div>

          {/* Active Status Toggle (Disabled / Enabled by Default) */}
          <div className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-gray-200 dark:border-slate-700/70">
            <div>
              <span className="text-xs font-black text-gray-900 dark:text-white block">
                Department Active Status
              </span>
              <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                Active status is enabled by default for all departments
              </span>
            </div>

            <button
              type="button"
              disabled={true}
              title="Department active status is enabled by default"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-xs opacity-75 cursor-not-allowed bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
            >
              <FaToggleOn size={16} />
              <span>Active</span>
            </button>
          </div>

          {/* Actions Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-gray-100 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-xs font-black text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md shadow-blue-500/20 disabled:opacity-50 transition flex items-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaCheck size={11} /> {isEdit ? "Update Department" : "Create Department"}
                </>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
